import {Category} from 'src/interfaces/Template.interface'

export type FlatFieldTreeItem = {
  id: number
  api_name: string
  api_id: string
  api_code?: string
  platform_field: number
  parent_data: number | null
  [key: string]: any
}

export type FieldTreeNode = Category & {
  children: FieldTreeNode[]
  isLeaf: boolean
  item?: FlatFieldTreeItem
  hasFetched?: boolean
  isExpanded?: boolean
  loading?: boolean
  fullPath?: string
}

// Build a nested tree from flat /api/get-field-tree results by splitting api_name on "->"
export const buildFieldTree = (items: FlatFieldTreeItem[]): FieldTreeNode[] => {
  const rootMap = new Map<string, FieldTreeNode>()
  const pathMap = new Map<string, FieldTreeNode>()
  let syntheticId = -1

  const getOrCreate = (parts: string[], idx: number, base?: FlatFieldTreeItem) => {
    const key = parts.slice(0, idx + 1).join(' / ')
    
    const isLeaf = idx === parts.length - 1
    const isImmediateParent = idx === parts.length - 2
    
    let expectedId = syntheticId
    if (isLeaf && base?.id) {
      expectedId = base.id
    } else if (isImmediateParent && base?.parent_data) {
      expectedId = base.parent_data
    } else {
      expectedId = syntheticId--
    }

    if (pathMap.has(key)) {
      const existing = pathMap.get(key)!
      if (existing.id < 0 && expectedId > 0) {
        existing.id = expectedId
      }
      if (isLeaf) {
        existing.isLeaf = true
        existing.item = base
        existing.api_id = base?.api_id || existing.api_id
        existing.api_code = base?.api_code || existing.api_code
      }
      return existing
    }

    const node: FieldTreeNode = {
      id: expectedId,
      api_name: parts[idx],
      api_id: base?.api_id || '',
      api_code: base?.api_code || '',
      updated_at: '',
      created_at: '',
      platform: 0,
      platform_field: base?.platform_field || 0,
      parent_data: isLeaf ? (base?.parent_data ?? null) : null,
      child_data: [],
      children: [],
      isLeaf,
      item: isLeaf ? base : undefined,
      hasFetched: true,
      isExpanded: !isLeaf,
      loading: false,
      fullPath: isLeaf ? base?.api_name : undefined,
    }

    pathMap.set(key, node)
    if (idx === 0) {
      rootMap.set(key, node)
    } else {
      const parentKey = parts.slice(0, idx).join(' / ')
      const parent = pathMap.get(parentKey)
      if (parent) parent.children.push(node)
    }
    return node
  }

  items.forEach((item) => {
    const parts = item.api_name.split('->').map((p) => p.trim()).filter(Boolean)
    parts.forEach((_, idx) => getOrCreate(parts, idx, item))
  })

  return Array.from(rootMap.values())
}
